/* ==========================================================================
   TABULA RASA — theme behavior
   - Intercepts internal link clicks and swaps #tr-main via fetch, so page
     changes ("Look Book", "Staff", etc.) play a transition instead of a
     hard reload. Falls back to a normal navigation for external links,
     modified clicks, cart/account/password links, and on fetch failure.
   - Transition style ("smooth" | "glitch") is read from
     body[data-transition-style], set by Theme Settings.
   - Before swapping content, syncs any <link rel="stylesheet"> the new
     page needs but the current page hasn't loaded yet (Shopify only loads
     the CSS for sections actually present on a given page, so navigating
     between pages with different sections needs this or new content
     renders unstyled — oversized images, no layout — until a real reload).
   - Adds a gentle mouse-parallax "floating in water" tilt to .tr-glass
     elements.
   ========================================================================== */
(function () {
  "use strict";

  var main = document.querySelector("[data-tr-main]");
  var overlay = document.querySelector(".tr-glitch-overlay");
  var body = document.body;

  function transitionStyle() {
    return body.getAttribute("data-transition-style") || "smooth";
  }

  function isInternalNavigable(a) {
    if (!a || !a.href) return false;
    if (a.target && a.target !== "" && a.target !== "_self") return false;
    if (a.hasAttribute("download")) return false;
    if (a.dataset.trNoTransition !== undefined) return false;
    var url;
    try { url = new URL(a.href, window.location.href); } catch (e) { return false; }
    if (url.origin !== window.location.origin) return false;
    if (url.pathname === window.location.pathname && url.hash) return false; // in-page anchor
    var skip = ["/cart", "/account", "/password", "/checkout"];
    for (var i = 0; i < skip.length; i++) {
      if (url.pathname.indexOf(skip[i]) === 0) return false;
    }
    return true;
  }

  /* Load any stylesheet the new page references that isn't already on this
     page, and wait for it to finish loading (or fail) before continuing —
     this is what prevents the "huge unstyled images until refresh" bug. */
  function syncHeadAssets(newDoc) {
    var currentHrefs = Array.prototype.map.call(
      document.querySelectorAll('link[rel="stylesheet"][href]'),
      function (l) { return l.href; }
    );
    var newLinks = newDoc.querySelectorAll('link[rel="stylesheet"][href]');
    var pending = [];
    newLinks.forEach(function (link) {
      if (currentHrefs.indexOf(link.href) === -1) {
        var clone = document.createElement("link");
        clone.rel = "stylesheet";
        clone.href = link.href;
        pending.push(
          new Promise(function (resolve) {
            clone.addEventListener("load", resolve, { once: true });
            clone.addEventListener("error", resolve, { once: true }); // never block the transition on a failed stylesheet
          })
        );
        document.head.appendChild(clone);
      }
    });
    return Promise.all(pending);
  }

  function swapPage(url) {
    if (!main) { window.location.href = url; return; }
    var style = transitionStyle();
    main.classList.add("tr-leaving");

    if (style === "glitch" && overlay) {
      overlay.classList.add("tr-active");
    }

    var outDuration = style === "glitch" ? 420 : 650;

    fetch(url, { credentials: "same-origin" })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var newMain = doc.querySelector("[data-tr-main]");
        if (!newMain) { window.location.href = url; return; }

        // Kick off loading any missing stylesheets now, in parallel with the
        // outgoing animation, so they're ready (or already loading) by the
        // time the new content is actually inserted.
        var assetsReady = syncHeadAssets(doc);

        setTimeout(function () {
          assetsReady.then(function () {
            main.innerHTML = newMain.innerHTML;
            document.title = doc.title;
            window.history.pushState({ trUrl: url }, "", url);
            main.classList.remove("tr-leaving");
            main.classList.add("tr-entering");
            window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });

            if (style === "glitch" && overlay) {
              overlay.classList.remove("tr-active");
              void overlay.offsetWidth; // restart animation
              overlay.classList.add("tr-active");
            }

            bindLinks(); // re-bind links inside the newly swapped content
            if (window.Shopify && window.Shopify.onFormRefresh) { /* no-op hook */ }

            setTimeout(function () {
              main.classList.remove("tr-entering");
              if (overlay) overlay.classList.remove("tr-active");
            }, 520);
          });
        }, outDuration);
      })
      .catch(function () {
        window.location.href = url;
      });
  }

  function onClick(e) {
    var a = e.target.closest("a");
    if (!a) return;
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!isInternalNavigable(a)) return;
    e.preventDefault();
    swapPage(a.href);
  }

  function bindLinks() {
    // Delegated listener is bound once on document; nothing to re-bind per
    // element, but kept as a hook in case future sections need per-element setup.
  }

  document.addEventListener("click", onClick);

  /* ---- in-page hash links: never navigate, either swap a panel or smooth-scroll ---- */
  function switchPanel(target) {
    var current = document.querySelector(".tr-panel--active");
    if (current === target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    var style = transitionStyle();
    var leaveClass = style === "glitch" ? "tr-leaving" : "tr-panel-fly-out";
    var enterClass = style === "glitch" ? "tr-entering" : "tr-panel-fly-in";
    if (current) current.classList.add(leaveClass);
    if (style === "glitch" && overlay) overlay.classList.add("tr-active");
    var outDuration = style === "glitch" ? 420 : 300;
    setTimeout(function () {
      if (current) current.classList.remove("tr-panel--active", leaveClass);
      target.classList.add("tr-panel--active", enterClass);
      window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
      if (style === "glitch" && overlay) {
        overlay.classList.remove("tr-active");
        void overlay.offsetWidth;
        overlay.classList.add("tr-active");
      }
      setTimeout(function () {
        target.classList.remove(enterClass);
        if (overlay) overlay.classList.remove("tr-active");
      }, 500);
    }, outDuration);
  }

  document.addEventListener("click", function (e) {
    var a = e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute("href").slice(1);
    if (!id) return;
    var target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    if (target.classList.contains("tr-panel")) {
      switchPanel(target);
    } else {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });

  window.addEventListener("popstate", function (e) {
    if (e.state && e.state.trUrl) {
      swapPage(e.state.trUrl);
    } else {
      window.location.reload();
    }
  });

  /* ---- gentle "submerged" parallax on glass surfaces ---- */
  var glassEls = document.querySelectorAll(".tr-glass, .tr-float");
  if (glassEls.length && window.matchMedia("(pointer:fine)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    document.addEventListener("mousemove", function (e) {
      var cx = window.innerWidth / 2;
      var cy = window.innerHeight / 2;
      var dx = (e.clientX - cx) / cx;
      var dy = (e.clientY - cy) / cy;
      glassEls.forEach(function (el, i) {
        var depth = 1 + (i % 3) * 0.4;
        el.style.transform =
          "translate(" + (dx * 3 * depth).toFixed(2) + "px," + (dy * 3 * depth).toFixed(2) + "px)";
      });
    });
  }

  /* ---- password / intake form: block real submit if action is placeholder ---- */
  var intakeForm = document.querySelector("[data-tr-intake-form]");
  if (intakeForm) {
    intakeForm.addEventListener("submit", function () {
      // Real submission is handled by Shopify's customer/contact form action —
      // nothing to intercept. This hook is left for adding a confirmation
      // animation later without touching Liquid.
    });
  }
})();